import React, { useState, useEffect } from 'react';
import { motion, type Variants } from 'framer-motion';
import { CalendarDays, Clock, ExternalLink, Lock } from 'lucide-react';
import { GhostPost } from '@/lib/ghostService';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { cn } from '@/utils/cn';
import { APP_URLS } from '@/config/urls';

interface BlogPostPreviewProps {
  post: GhostPost;
  index: number;
}

export default function BlogPostPreview({ post, index }: BlogPostPreviewProps) {
  const defaultEase: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

  // Format the published date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    // Format to Slovak style date
    return date.toLocaleDateString('sk-SK', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Animation variants for card elements
  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5,
        delay: index * 0.1,
        ease: defaultEase
      }
    },
    hover: {
      y: -4,
      scale: 1.02,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 15
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.7, delay: index * 0.1 + 0.3 }
    },
    hover: { 
      scale: 1.1,
      transition: { duration: 0.4 }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5, 
        delay: index * 0.1 + 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const tagVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3, delay: index * 0.1 + 0.4 }
    }
  };

  // AI-related themed placeholder images with a unified aesthetic
  const placeholderImages = [
    'https://images.unsplash.com/photo-1677442135966-736432af954a?q=80&w=800&auto=format&fit=crop', // AI digital brain
    'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=800&auto=format&fit=crop', // Tech related
    'https://images.unsplash.com/photo-1591453089816-0fbb971b454c?q=80&w=800&auto=format&fit=crop', // Data visualization
    'https://images.unsplash.com/photo-1516110833967-0b5716ca1387?q=80&w=800&auto=format&fit=crop', // Tech office
    "data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22800%22%20height%3D%22400%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20800%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cdefs%3E%3Cstyle%20type%3D%22text%2Fcss%22%3E%23holder_15e8d59c363%20text%20%7B%20fill%3A%23AAA%3Bfont-weight%3Anormal%3Bfont-family%3AHelvetica%2C%20monospace%3Bfont-size%3A40pt%20%7D%20%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22holder_15e8d59c363%22%3E%3Crect%20width%3D%22800%22%20height%3D%22400%22%20fill%3D%22%23F5F5F5%22%3E%3C%2Frect%3E%3Cg%3E%3Ctext%20x%3D%22285.9296875%22%20y%3D%22217.75625%22%3EBlog%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E"
  ];

  // State to track which fallback image to use and the current image URL
  const [fallbackIndex, setFallbackIndex] = useState(0);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [attemptedUrls, setAttemptedUrls] = useState<string[]>([]);

  // Process image URL to fix domain issues
  const processImageUrl = (url: string): string => {
    if (!url) return '';

    // Handle internal Ghost domain URLs
    if (url.includes('ghost-b88ko48wg488g8ccsgg4kws8.167.235.31.245.sslip.io')) {
      // For images in Ghost content directory
      if (url.includes('/content/images/')) {
        // Extract the path after domain and create with public domain
        const pathMatch = url.match(/\/content\/images\/(.+)/);
        if (pathMatch && pathMatch[1]) {
          return `${APP_URLS.BLOG}/content/images/${pathMatch[1]}`;
        }
      }
      
      // General domain replacement
      const fixedUrl = url.replace(
        'ghost-b88ko48wg488g8ccsgg4kws8.167.235.31.245.sslip.io',
        'blog.ai-kurzy.online'
      );
      return fixedUrl;
    }
    
    return url;
  };

  // Enhanced image handling
  useEffect(() => {
    // Reset state for new post
    setImageLoaded(false);
    setAttemptedUrls([]);
    
    if (post.feature_image) {
      // Process the image URL to fix domain issues
      const processedUrl = processImageUrl(post.feature_image);
      setImageUrl(processedUrl);
      setAttemptedUrls([processedUrl]);
    } else {
      // If no feature image, use a themed placeholder based on post ID
      // This creates a consistent placeholder for the same post
      const placeholderIndex = Math.abs(
        post.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      ) % placeholderImages.length;
      
      setFallbackIndex(placeholderIndex);
      setImageUrl(placeholderImages[placeholderIndex]);
    }
  }, [post.id, post.title, post.feature_image]);

  // Function to try next fallback image
  const tryNextFallback = () => {
    setImageLoaded(false);
    const nextIndex = (fallbackIndex + 1) % placeholderImages.length;
    setFallbackIndex(nextIndex);
    setImageUrl(placeholderImages[nextIndex]);
  };

  // Function to try alternative image URLs
  const tryAlternativeUrl = (currentUrl: string) => {
    setImageLoaded(false);
    
    // Skip if we've already tried this URL
    if (attemptedUrls.includes(currentUrl)) {
      tryNextFallback();
      return;
    }
    
    // Add current URL to attempted list
    setAttemptedUrls(prev => [...prev, currentUrl]);
    
    let alternativeUrl = '';
    
    // If URL contains the Ghost domain, try different protocol
    if (currentUrl.includes('blog.ai-kurzy.online')) {
      if (currentUrl.startsWith('https:')) {
        // Try HTTP instead
        alternativeUrl = currentUrl.replace('https:', 'http:');
      } else if (currentUrl.startsWith('http:')) {
        // Try HTTPS instead
        alternativeUrl = currentUrl.replace('http:', 'https:');
      }
    }
    
    if (alternativeUrl && !attemptedUrls.includes(alternativeUrl)) {
      setImageUrl(alternativeUrl);
      setAttemptedUrls(prev => [...prev, alternativeUrl]);
    } else {
      // If no alternatives or already tried, use fallback
      tryNextFallback();
    }
  };

  return (
    <motion.div
      className="h-full"
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      whileHover="hover"
      viewport={{ once: true, margin: "-50px" }}
    >
      <Card className={cn(
        "h-full overflow-hidden flex flex-col shadow-sm",
        "transition-all duration-300"
      )}>
        <div className="relative w-full h-52 overflow-hidden">
          <motion.div
            className="w-full h-full"
            variants={imageVariants}
          >
            {imageUrl ? (
              <img
                key={imageUrl} // Key helps force re-render when URL changes
                src={imageUrl}
                alt={post.title}
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                loading="eager" // Force eager loading for better visibility
                onLoad={() => {
                  setImageLoaded(true);
                }}
                onError={(e) => {
                  console.error('Image failed to load:', imageUrl);
                  // Add a data attribute to help identify which images failed
                  (e.target as HTMLImageElement).dataset.originalSrc = imageUrl;
                  
                  // If current image is from the post (not a fallback)
                  if (imageUrl && !placeholderImages.includes(imageUrl)) {
                    // First try an alternative URL before going to fallback
                    tryAlternativeUrl(imageUrl as string);
                  } else if (placeholderImages.includes(imageUrl as string)) {
                    // If even the fallback fails, try the next one
                    tryNextFallback();
                  } else {
                    // If all else fails, go to fallback
                    tryNextFallback();
                  }
                }}
              />
            ) : (
              // Show a shimmer loading effect while image is being determined
              <div className="w-full h-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 animate-pulse" />
            )}
          </motion.div>
          
          {/* Premium content indicator */}
          {(post.visibility === 'members' || post.visibility === 'paid' || post.access === false) && (
            <motion.div
              className="absolute top-4 left-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-xs font-bold py-2 px-3 rounded-lg flex items-center gap-1 shadow-lg border border-purple-500/30"
              variants={tagVariants}
            >
              <Lock className="h-3 w-3" />
              Premium
            </motion.div>
          )}
          
          {post.primary_tag && (
            <motion.div
              className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs font-medium py-1 px-2 rounded-md"
              variants={tagVariants}
            >
              {post.primary_tag.name}
            </motion.div>
          )}
        </div>
        
        <CardHeader className="px-6 py-4 pb-0">
          <motion.div
            className="flex items-center text-sm text-muted-foreground mb-2"
            variants={contentVariants}
          >
            <CalendarDays className="mr-1 h-4 w-4" />
            <span>{formatDate(post.published_at)}</span>
            <span className="mx-2">•</span>
            <Clock className="mr-1 h-4 w-4" />
            <span>{post.reading_time} min</span>
          </motion.div>
          
          <CardTitle className="text-xl font-bold mb-2 line-clamp-2">
            <motion.span variants={contentVariants}>
              <a href={`/blog/${post.slug}`} className="hover:text-primary transition-colors">
                {post.title}
              </a>
            </motion.span>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="px-6 py-3 flex-grow">
          <motion.div
            className="text-muted-foreground"
            variants={contentVariants}
          >
            {/* Display the excerpt with a "read more" ellipsis */}
            <p className="line-clamp-3">{post.excerpt}</p>
            
            {/* Hidden full content that can be revealed if needed */}
            <div
              className="hidden mt-4 text-sm prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: post.html }}
            />
          </motion.div>
        </CardContent>
        
        <CardFooter className="px-6 py-4 pt-2 border-t border-border flex items-center justify-between">
          <motion.div className="flex items-center" variants={contentVariants}>
            <img
              src="/ai_logo_light_transparent.png"
              alt="AI-Kurzy.ONLINE"
              className="w-8 h-8 mr-2"
              onError={(e) => {
                // Hide the image on error
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="text-sm font-medium">AI-Kurzy.ONLINE</span>
          </motion.div>
          
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="text-primary p-0 hover:bg-transparent"
          >
            <motion.a
              whileHover={{
                scale: 1.05,
                x: 3,
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              href={`/blog/${post.slug}`}
              // Now linking to our internal route instead of external Ghost blog URL
              className="flex items-center text-sm font-medium"
            >
              Čítať viac
              <ExternalLink className="ml-1 h-3 w-3" />
            </motion.a>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}
